import React, { useState } from "react";
import { motion } from "framer-motion";
import { useIsMobile } from "../../../hooks/useIsMobile";

interface KeywordContent {
    keyword: string;
    content: string;
}

interface KeywordProps {
    data: KeywordContent[];
}

const PILL_GRADIENT = 'linear-gradient(90deg, #19cf7a 0%, #00d695 100%)';

const Keyword = ({ data }: KeywordProps) => {
    const isMobile = useIsMobile();
    const [activeIndex, setActiveIndex] = useState(0);

    const validData = data.filter(d => d.keyword);
    if (validData.length === 0) return null;

    const activeContent = validData[activeIndex]?.content;

    if (isMobile) {
        return (
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <span className="text-[#19cf7a] text-[14px] font-semibold">작가 PICK</span>
                    <span className="text-[24px] font-bold text-black">작품 키워드 3가지</span>
                </div>

                <div className="flex flex-row flex-wrap gap-2">
                    {validData.map((word, i) => (
                        <motion.button
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.45, delay: i * 0.1, ease: "easeOut" }}
                            className={`h-[36px] px-4 rounded-[10px] text-[12px] font-bold text-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)] transition-all ${
                                activeIndex === i
                                    ? 'opacity-100 ring-2 ring-white/60 scale-105'
                                    : 'opacity-80'
                            }`}
                            style={{ background: activeIndex === i ? PILL_GRADIENT : '#1a1a1a' }}
                            onClick={() => setActiveIndex(i)}
                        >
                            {word.keyword}
                        </motion.button>
                    ))}
                </div>

                {activeContent && (
                    <motion.div
                        key={activeIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="bg-[#f8f9fa] border border-[#e9ecef] rounded-[16px] px-4 pt-4 pb-4 w-full"
                    >
                        <p className="font-namsan text-[#6a6a6a] text-[14px] leading-[24px]">
                            {activeContent}
                        </p>
                    </motion.div>
                )}
            </div>
        );
    }

    // Desktop
    return (
        <div className="flex flex-col gap-3 w-full">
            <div className="flex flex-col gap-1 mt-12">
                <p className="text-black text-[16px] font-semibold">작가 PICK</p>
                <p className="text-[40px] font-bold text-black">작품 키워드 3가지</p>
            </div>

            <div className="flex flex-row gap-4">
                {validData.map((word, i) => (
                    <motion.button
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        whileHover={{ boxShadow: '0 8px 16px -4px rgba(0,0,0,0.12)', scale: activeIndex === i ? 1.05 : 1.02, transition: { duration: 0.18, ease: "easeOut" } }}
                        transition={{ duration: 0.45, delay: i * 0.12, ease: "easeOut" }}
                        className={`h-[50px] px-7 rounded-xl text-[18px] font-bold text-white shadow-md transition-shadow cursor-pointer ${
                            activeIndex === i
                                ? 'ring-2 ring-white/60 scale-105'
                                : ''
                        }`}
                        style={{ background: activeIndex === i ? PILL_GRADIENT : '#1a1a1a' }}
                        onClick={() => setActiveIndex(i)}
                    >
                        {word.keyword}
                    </motion.button>
                ))}
            </div>

            {activeContent && (
                <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-[#f3f3f3] border border-[#bcbcbc] rounded-[18px] px-7 py-5 w-full shadow-[0px_1px_3px_0px_rgba(0,0,0,0.05)]"
                >
                    <p className="font-namsan text-[#6A6A6A] text-[18px] leading-[32px]">
                        {activeContent}
                    </p>
                </motion.div>
            )}
        </div>
    );
};

export default Keyword;
